/**
 * Spec 004 US1 (T011) — Status legend trigger + tooltip pane.
 *
 * Renders a small "?" info-icon next to the /expenses page title.
 * On hover OR focus, surfaces a panel describing every entry of the
 * 8-state closed set defined in spec.md FR-G1-STATUS-COLOR. Keyboard
 * accessible: Enter / Space toggles, Escape closes.
 *
 * Lightweight — uses a controlled `useState` over the existing tint
 * palette tokens; no new design primitive required.
 */
import { useState, useRef, useEffect } from 'react';
import { SURFACE } from '../ui';
import type { ExpenseRowStatus } from '@armard/shared';

const LEGEND_ITEMS: ReadonlyArray<{
  state: Exclude<ExpenseRowStatus, 'default'>;
  label: string;
  tintClass: string;
  blurb: string;
}> = [
  {
    state: 'quartal-closed',
    label: 'Quartal abgeschlossen',
    tintClass: 'bg-blue-500/10 border border-blue-500/30 text-blue-200',
    blurb: 'Beleg liegt in einem gesperrten Quartal — keine Aktion möglich.',
  },
  // Codex Wave-C finding #1 (high) — `ocr-failure` is impossible to
  // surface on the list because `processingPhase` isn't projected
  // into the list DTO. Removed from the legend entirely so users
  // aren't misled into looking for an ocr-failure tint that never
  // fires. The helper still supports the state (detail-view
  // consumers + future LEFT JOIN projection via
  // `T-cdx-list-processing-phase`). When the projection lands, this
  // legend entry can come back in.
  {
    state: 'betragsabweichung',
    label: 'Betragsabweichung',
    tintClass: 'bg-rose-500/10 border border-rose-500/40 text-rose-200',
    blurb:
      'GiroCode/EPC-QR oder eRechnung-XML widerspricht dem erkannten Rechnungsbetrag.',
  },
  {
    state: 'overdue',
    label: 'Überfällig',
    tintClass: 'bg-red-500/15 border border-red-500/40 text-red-200',
    blurb: 'Fälligkeitsdatum überschritten und noch nicht bezahlt.',
  },
  {
    state: 'missing-date',
    label: 'Datum fehlt',
    tintClass: 'bg-orange-500/10 border border-orange-500/30 text-orange-200',
    blurb:
      'Rechnungsdatum nicht erkannt — bitte manuell setzen, sonst keine Quartal-Zuordnung.',
  },
  {
    state: 'missing-fields',
    label: 'Felder fehlen',
    tintClass: 'bg-orange-500/10 border border-orange-500/30 text-orange-200',
    blurb: 'Lieferant, Kategorie oder Bruttobetrag noch nicht gesetzt.',
  },
  {
    state: 'paid',
    label: 'Bezahlt',
    tintClass: 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-200',
    blurb: 'Beleg ist bezahlt; Zahlungsdatum gesetzt.',
  },
];

export function ExpenseStatusLegendTrigger() {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const clickAway = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', handler);
    document.addEventListener('mousedown', clickAway);
    return () => {
      document.removeEventListener('keydown', handler);
      document.removeEventListener('mousedown', clickAway);
    };
  }, [open]);

  return (
    <span ref={containerRef} className="relative inline-flex">
      <button
        type="button"
        aria-label="Legende für Status-Tints anzeigen"
        aria-expanded={open}
        data-testid="status-legend-trigger"
        /* Codex Wave-D + e2e fix: a single Playwright `.click()` performs
           hover-then-click. The previous wiring opened on hover, then
           the click toggled it closed → content vanishes. Click now
           always OPENS; outside-click / Escape closes. Hover still
           previews. */
        onClick={() => setOpen(true)}
        onMouseEnter={() => setOpen(true)}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
        className="inline-flex items-center justify-center w-6 h-6 rounded-full border border-white/20 text-white/60 hover:text-white hover:border-white/40 text-sm font-normal transition-colors"
      >
        ?
      </button>
      {open && (
        <div
          role="dialog"
          aria-label="Status-Legende"
          data-testid="status-legend-content"
          className={`absolute z-30 top-full left-0 mt-2 w-80 p-3 rounded-lg ${SURFACE.solid}`}
        >
          <h2 className="text-xs uppercase tracking-wider text-white/50 mb-2">
            Status-Legende
          </h2>
          <ul className="space-y-2">
            {LEGEND_ITEMS.map((item) => (
              <li
                key={item.state}
                className="flex items-start gap-2 text-xs text-white/80"
                data-testid={`status-legend-item-${item.state}`}
              >
                <span
                  className={`inline-flex flex-shrink-0 items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${item.tintClass}`}
                >
                  {item.label}
                </span>
                <span className="text-white/70 leading-snug">{item.blurb}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </span>
  );
}
